import DataTable from "components/DataTable";
import React, { useCallback, useContext, useEffect, useState } from "react";
import apiService from "services/apiService";
import AddSalary from "./AddSalary";
import GenericModal from "components/Modal";
import { FaEdit, FaEye } from "react-icons/fa";
import { AiOutlineExport } from "react-icons/ai";
import { Calculator } from "lucide-react";
import ReactMarkdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import UpdateSalaryDetails from "./UpdateSalaryDetails";
import AddAppraisal from "../apprial/Addappraisal";
import { InputActionMeta } from "react-select";
import { sortByKey } from "util/sortOptions";
import * as XLSX from "xlsx";
import CommonListingFilter, {
  CommonFilterField,
  CommonFilterOption,
} from "components/CommonListingFilter/CommonListingFilter";
import { BannerContext } from "components/SideBar";
import {
  SalaryDetailView,
  SalaryFormValues,
  inr,
  epochToDisplay,
  structureTypeLabel,
  resolveSalaryMode,
  getGrossSalary,
  getContractValue,
} from "./ViewSalary";

interface OptionProps {
  value: string;
  label: string;
}

interface UserSummary {
  email?: string;
  displayName?: string;
}

type SalaryPayload = {
  content?: SalaryFormValues[];
  salary?: SalaryFormValues[];
};

// Salary domain types, formatting helpers and the detail view now live in
// ./ViewSalary (imported above) so the listing table and the modal view share
// one source of truth for the discriminated (regular/contractor/trainee) shape.

// Explicit styling for the markdown-rendered "Salary Calculation Guide".
// Defined at module scope for a stable object identity across renders. The
// Tailwind typography (`prose`) plugin is not installed in this project, so
// table spacing, borders and heading rhythm are applied here instead — this is
// what gives the guide's tables proper padding between the Field/Label columns.
const guideMarkdownComponents: Components = {
  h1: ({ children }) => (
    <h1 className="text-xl font-bold text-slate-900 dark:text-white mt-6 mb-3 first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-lg font-semibold text-slate-900 dark:text-white mt-6 mb-2 first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-base font-semibold text-slate-900 dark:text-white mt-5 mb-2">{children}</h3>
  ),
  p: ({ children }) => (
    <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300 my-2">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="list-disc pl-6 my-2 space-y-1 text-sm text-slate-700 dark:text-slate-300">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-6 my-2 space-y-1 text-sm text-slate-700 dark:text-slate-300">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => (
    <strong className="font-semibold text-slate-900 dark:text-white">{children}</strong>
  ),
  hr: () => <hr className="my-4 border-slate-200 dark:border-slate-700" />,
  a: ({ href, children }) => (
    <a href={href} className="text-primary hover:underline" target="_blank" rel="noreferrer">
      {children}
    </a>
  ),
  code: ({ children }) => (
    <code className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-gray-700 font-mono text-[13px] text-slate-800 dark:text-slate-200">
      {children}
    </code>
  ),
  // Code blocks: neutralise the inline <code> background/padding above so the
  // block reads as a single panel rather than a nested highlight.
  pre: ({ children }) => (
    <pre className="my-3 p-3 rounded-lg bg-slate-100 dark:bg-gray-700 overflow-x-auto font-mono text-[13px] text-slate-800 dark:text-slate-200 [&_code]:bg-transparent [&_code]:p-0 [&_code]:text-inherit">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="my-4 overflow-x-auto">
      <table className="min-w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-50 dark:bg-gray-700/50">{children}</thead>,
  th: ({ children }) => (
    <th className="border border-slate-200 dark:border-slate-700 px-4 py-2 text-left font-semibold text-slate-700 dark:text-slate-200 align-top whitespace-nowrap">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-slate-200 dark:border-slate-700 px-4 py-2 text-left align-top text-slate-700 dark:text-slate-300">
      {children}
    </td>
  ),
};

const SalaryDetails = () => {
  const [loading, setLoading] = useState(false);
  const [salaryDetails, setSalaryDetails] = useState<SalaryFormValues[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [updatedData, setUpdatedData] = useState<SalaryFormValues | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [appraisalData, setAppraisalData] = useState<SalaryFormValues | null>(null);
  const [isAppraisal, setIsAppraisal] = useState(false);
  const [viewData, setViewData] = useState<SalaryFormValues | null>(null);
  const [isViewing, setIsViewing] = useState(false);

  // Salary structure calculation guide (markdown) modal states
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [guideContent, setGuideContent] = useState("");
  const [guideLoading, setGuideLoading] = useState(false);
  const [guideError, setGuideError] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  // User Filter States
  const [selectedUser, setSelectedUser] = useState<OptionProps | null>(null);
  const [draftSelectedUser, setDraftSelectedUser] = useState<OptionProps | null>(null);
  const [userOptions, setUserOptions] = useState<OptionProps[]>([]);
  const [userPage, setUserPage] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const USER_PAGE_SIZE = 10;

  // Structure Filter States
  const [structureOptions, setStructureOptions] = useState<OptionProps[]>([]);
  const [loadingStructures, setLoadingStructures] = useState(false);
  const [selectedStructure, setSelectedStructure] = useState<OptionProps | null>(null);
  const [draftSelectedStructure, setDraftSelectedStructure] = useState<OptionProps | null>(null);

  const getUsersFromPayload = (payload: unknown): UserSummary[] => {
    if (Array.isArray(payload)) {
      return payload as UserSummary[];
    }

    if (
      typeof payload === "object" &&
      payload !== null &&
      "content" in payload &&
      Array.isArray((payload as { content?: unknown }).content)
    ) {
      return (payload as { content: UserSummary[] }).content;
    }

    return [];
  };

  const getSalaryDataFromPayload = (payload: unknown): SalaryFormValues[] => {
    if (Array.isArray(payload)) {
      return payload as SalaryFormValues[];
    }

    const typedPayload = payload as SalaryPayload;
    if (Array.isArray(typedPayload?.content)) {
      return typedPayload.content;
    }

    if (Array.isArray(typedPayload?.salary)) {
      return typedPayload.salary;
    }

    return [];
  };

  // Fetch Salary Structure Options
  useEffect(() => {
    setLoadingStructures(true);
    apiService
      .get("api/salary/getAllSalaryStructures")
      .then((response) => {
        const payload = response.data;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let data: any[] = [];

        // Safely extract the data depending on how the backend wraps it
        if (Array.isArray(payload)) {
          data = payload;
        } else if (payload && Array.isArray(payload.content)) {
          data = payload.content;
        } else if (payload && Array.isArray(payload.data)) {
          data = payload.data;
        } else if (payload && payload.structureName) {
          // If the API returns a single object instead of an array
          data = [payload];
        }

        // Map the extracted data to dropdown options
        const options = data
          .filter((item) => item?.structureName) // Ensure structureName exists
          .map((item) => ({
            value: item.structureName,
            label: item.structureName,
          }));

        setStructureOptions(options);
      })
      .catch((error) => {
        console.error("Failed to fetch salary structures", error);
      })
      .finally(() => {
        setLoadingStructures(false);
      });
  }, []);

  const fetchUsers = async (
    nextPage: number,
    searchValue: string,
    append: boolean
  ) => {
    if (loadingUsers) return;

    setLoadingUsers(true);
    try {
      const query = new URLSearchParams();
      query.set("page", String(nextPage));
      query.set("size", String(USER_PAGE_SIZE));
      if (searchValue.trim()) {
        query.set("search", searchValue.trim());
      }

      const res = await apiService.get(`api/users?${query.toString()}`);
      const payload = res.data;
      const users = getUsersFromPayload(payload);

      const typedUsers: UserSummary[] = users;
      const options = typedUsers
        .filter((item) => item?.email)
        .map((item) => ({
          value: item.email,
          label: item.displayName || item.email,
        }));

      setUserOptions((prev) => {
        const merged = append ? [...prev, ...options] : options;
        const dedup = new Map<string, OptionProps>();
        merged.forEach((item) => {
          if (!dedup.has(item.value)) {
            dedup.set(item.value, item);
          }
        });
        return sortByKey(Array.from(dedup.values()), "label");
      });

      setUserPage(nextPage);
      if (typeof payload?.last === "boolean") {
        setHasMoreUsers(!payload.last);
      } else if (typeof payload?.totalPages === "number") {
        setHasMoreUsers(nextPage < payload.totalPages - 1);
      } else {
        setHasMoreUsers(options.length === USER_PAGE_SIZE);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void fetchUsers(0, userSearch, false);
    }, 350);

    return () => window.clearTimeout(timer);
  }, [userSearch]);

  // Main Data Fetch for Datatable
  useEffect(() => {
    setLoading(true);
    const activeSearch = selectedUser?.value || searchTerm;
    const query = new URLSearchParams();

    if (activeSearch.trim()) {
      query.set("search", activeSearch.trim());
    }

    let apiUrl = "";

    // Switch to byStructure endpoint if a structure filter is applied
    if (selectedStructure?.value) {
      query.set("structureName", selectedStructure.value);
      // Construct URL WITHOUT page and size parameters
      apiUrl = `api/salary/byStructure?${query.toString()}`;
    } else {
      // For the default endpoint, we include the pagination parameters
      query.set("page", String(page));
      query.set("size", String(size));
      apiUrl = `api/salary?${query.toString()}`;
    }

    apiService
      .get(apiUrl)
      .then((response) => {
        const payload = response.data;
        const data = getSalaryDataFromPayload(payload);

        const apiTotalElements =
          typeof payload?.totalElements === "number"
            ? payload.totalElements
            : data.length;

        setSalaryDetails(data);
        setTotalElements(apiTotalElements);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [refresh, page, size, searchTerm, selectedUser?.value, selectedStructure?.value]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleEdit = (row: any) => {
    setIsUpdating(true);
    setUpdatedData(row);
  };

  const handleAppraisal = (row: object) => {
    setIsAppraisal(true);
    setAppraisalData(row as SalaryFormValues);
  };

  // Open full-detail view modal for the selected salary row
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleView = (row: any) => {
    setViewData(row as SalaryFormValues);
    setIsViewing(true);
  };

  // Client-side export logic for Salary Data
  const handleExport = () => {
    if (!salaryDetails || salaryDetails.length === 0) {
      console.warn("No data available to export");
      return;
    }

    // Export the fields common to every employment type (type-specific figures
    // now live in the nested regular/contractor/trainee objects).
    const exportData = salaryDetails.map((row) => ({
      "User Name": row.userName || "-",
      "Email": row.employeeEmail || "-",
      "Type": structureTypeLabel(row),
      "Structure Name": row.salaryStructure?.structureName || "-",
      "Contract Value / CTC": getContractValue(row) ?? 0,
      "Gross Salary": getGrossSalary(row) ?? 0,
      "Net Take Home": row.netTakeHome || 0,
      "TDS": row.tds || 0,
      "Last Modified": epochToDisplay(row.modifiedDate),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    worksheet["!cols"] = [
      { wch: 25 },
      { wch: 30 },
      { wch: 14 },
      { wch: 22 },
      { wch: 18 },
      { wch: 15 },
      { wch: 15 },
      { wch: 14 },
      { wch: 16 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Salary Details");
    XLSX.writeFile(workbook, "Salary_Details.xlsx");
  };

  // Open the calculation guide modal and fetch the markdown guide.
  // The endpoint returns a plain markdown string (text/markdown), so we
  // request it as text and render it with ReactMarkdown.
  const handleOpenGuide = () => {
    setIsGuideOpen(true);

    // Fetch only once; keep the content cached for subsequent opens.
    if (guideContent || guideLoading) return;

    setGuideLoading(true);
    setGuideError("");
    apiService
      .get("api/salary/structure-guide", {
        responseType: "text",
        headers: { Accept: "text/markdown" },
      })
      .then((response) => {
        setGuideContent(
          typeof response.data === "string"
            ? response.data
            : String(response.data ?? ""),
        );
      })
      .catch((error) => {
        console.error("Failed to fetch salary structure guide:", error);
        setGuideError("Failed to load the calculation guide. Please try again.");
      })
      .finally(() => {
        setGuideLoading(false);
      });
  };

  // The salary payload is now discriminated by employment type (regular /
  // contractor / trainee), so type-specific figures (Annual CTC, Basic, PF…)
  // are null for most rows. The table therefore shows only fields common to
  // every type; the full, type-aware breakdown lives in the View modal.
  const columns = [
    { key: "userName", label: "Employee Name", bold: true },
    {
      key: "type",
      label: "Type",
      bold: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (_value: React.ReactNode, row: any) => {
        const mode = resolveSalaryMode(row as SalaryFormValues);
        const cls =
          mode === "contract"
            ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
            : mode === "trainee"
              ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
              : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
        return (
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${cls}`}>
            {structureTypeLabel(row as SalaryFormValues)}
          </span>
        );
      },
    },
    {
      key: "salaryStructure.structureName",
      label: "Structure Name",
      bold: true,
      // Add a custom render function to safely extract the nested value
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (_value: React.ReactNode, row: any) => {
        return <>{row?.salaryStructure?.structureName || "-"}</>;
      },
    },
    {
      key: "grossSalary",
      label: "Gross Salary",
      bold: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (_value: React.ReactNode, row: any) => (
        <>{inr(getGrossSalary(row as SalaryFormValues))}</>
      ),
    },
    {
      key: "netTakeHome",
      label: "Net Take Home",
      bold: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (_value: React.ReactNode, row: any) => <>{inr(row?.netTakeHome)}</>,
    },
    {
      key: "tds",
      label: "TDS",
      bold: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (_value: React.ReactNode, row: any) => <>{inr(row?.tds)}</>,
    },
    {
      key: "modifiedDate",
      label: "Last Modified",
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (_value: React.ReactNode, row: any) => <>{epochToDisplay(row?.modifiedDate)}</>,
    },
    {
      key: "appraisal",
      label: "Appraisal",
      bold: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (_value: React.ReactNode, row: any) => {
        return (
          <button
            onClick={() => handleAppraisal(row)}
            className="p-2 transition-all hover:bg-primary/10 rounded-xl text-primary text-sm font-medium"
          >
            Appraisal
          </button>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      excludeExport: true,
      actions: (row: object) => (
        <div className="flex flex-start">
          {/* Eye button — opens full salary detail view */}
          <span
            onClick={() => handleView(row)}
            className="btn-secondary px-2 py-2 mr-2 rounded-lg cursor-pointer"
            title="View Details"
          >
            <FaEye />
          </span>
          <span
            onClick={() => handleEdit(row)}
            className="btn-primary px-2 py-2 mr-2 rounded-lg cursor-pointer"
            title="Edit"
          >
            <FaEdit />
          </span>
        </div>
      ),
    },
  ];

  const { isBannerVisible } = useContext(BannerContext);
  const dynamicHeightClass = isBannerVisible
    ? "h-[calc(100vh-110px)] min-h-[300px]"
    : "h-[calc(100vh-80px)] min-h-[300px]";

  const handleUserSearch = (
    inputValue: string,
    meta: InputActionMeta
  ): string => {
    if (meta.action === "input-change") {
      setUserSearch(inputValue);
      return inputValue;
    }

    if (meta.action === "menu-close") {
      setUserSearch("");
      return "";
    }

    return inputValue;
  };

  const loadMoreUsers = () => {
    if (hasMoreUsers && !loadingUsers) {
      void fetchUsers(userPage + 1, userSearch, true);
    }
  };

  const salaryFilterFields: CommonFilterField[] = [
    {
      id: "salary-user",
      type: "select",
      label: "User",
      placeholder: "All Users",
      widthClass: `min-w-[240px] ${draftSelectedStructure ? "!cursor-not-allowed [&_*]:!cursor-not-allowed" : ""}`,
      queryParam: "search",
      options: userOptions as CommonFilterOption[],
      value: draftSelectedUser as CommonFilterOption | null,
      isClearable: true,
      isSearchable: true,
      isLoading: loadingUsers,
      // Disable this dropdown if a Salary Structure is already selected in the draft
      isDisabled: !!draftSelectedStructure,
      onInputChange: handleUserSearch,
      onMenuScrollToBottom: loadMoreUsers,
      noOptionsMessage: () =>
        loadingUsers ? "Loading users..." : "No users found",
      onChange: (option) => {
        setDraftSelectedUser(option);
      },
    },
    {
      id: "salary-structure",
      type: "select",
      label: "Salary Structure",
      placeholder: "All Structures",
      widthClass: `min-w-[240px] ${draftSelectedUser ? "!cursor-not-allowed [&_*]:!cursor-not-allowed" : ""}`,
      options: structureOptions as CommonFilterOption[],
      value: draftSelectedStructure as CommonFilterOption | null,
      isClearable: true,
      isSearchable: true,
      isLoading: loadingStructures,
      // Disable this dropdown if a User is already selected in the draft
      isDisabled: !!draftSelectedUser,
      onChange: (option) => {
        setDraftSelectedStructure(option);
      },
    },
  ];

  const salaryFilterControls = (
    <CommonListingFilter
      fields={salaryFilterFields}
      panelTitle="Salary Filter"
      onApply={() => {
        setSelectedUser(draftSelectedUser);
        setSelectedStructure(draftSelectedStructure);
        setPage(0);
      }}
      onClear={() => {
        setDraftSelectedUser(null);
        setDraftSelectedStructure(null);
      }}
      onReset={() => {
        setDraftSelectedUser(null);
        setSelectedUser(null);
        setDraftSelectedStructure(null);
        setSelectedStructure(null);
        setPage(0);
      }}
    />
  );

  const handleSalaryPageChange = useCallback((nextPage: number) => {
    setPage(nextPage - 1);
  }, []);

  const handleSalaryItemsPerPageChange = useCallback((nextSize: number) => {
    setSize((prev) => (prev === nextSize ? prev : nextSize));
    setPage(0);
  }, []);

  const handleSalarySearchTermChange = useCallback((nextSearchTerm: string) => {
    setSearchTerm((prev) => (prev === nextSearchTerm ? prev : nextSearchTerm));
    setPage(0);
  }, []);

  return (
    <div className="text-slate-900 dark:text-white bg-white dark:bg-gray-800 transition-colors duration-300">
      <div className="bg-white dark:bg-gray-800">
        <DataTable
          heightClass={dynamicHeightClass}
          data={salaryDetails}
          columns={columns}
          onAddBtnClick={() => setIsAdding(true)}
          loading={loading}
          leftSideButtons={salaryFilterControls}
          serverSidePagination
          itemsPerPage={size}
          currentPage={page + 1}
          totalItems={totalElements}
          onPageChange={handleSalaryPageChange}
          onItemsPerPageChange={handleSalaryItemsPerPageChange}
          onSearchTermChange={handleSalarySearchTermChange}
          preAddButtons={
            <div className="flex items-center gap-3 mr-1">
              {/* Calculation guide — opens a modal with the salary structure
                  calculation information fetched as markdown */}
              <button
                onClick={handleOpenGuide}
                title="View Calculation Guide"
                type="button"
                className="inline-flex items-center justify-center h-[38px] px-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-500 transition-all shadow-sm"
              >
                <Calculator size={18} className="mr-2" />
                <span>Calculation</span>
              </button>
              <button
                onClick={handleExport}
                title="Export Salary Details"
                type="button"
                className="inline-flex items-center justify-center h-[38px] px-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-500 transition-all shadow-sm"
              >
                <AiOutlineExport size={18} className="mr-2" />
                <span>Export</span>
              </button>
            </div>
          }
        />
      </div>

      {isAdding && (
        <GenericModal
          isOpen={isAdding}
          onClose={() => setIsAdding(false)}
          header="Add Salary"
          customWidth="1024px"
        >
          <div className="bg-white dark:bg-gray-800">
            <AddSalary
              refresh={refresh}
              setRefresh={setRefresh}
              onClose={() => setIsAdding(false)}
            />
          </div>
        </GenericModal>
      )}
      {isUpdating && (
        <GenericModal
          isOpen={isUpdating}
          onClose={() => setIsUpdating(false)}
          header="Update Salary"
          customWidth="1024px"
        >
          {updatedData && (
            <div className="bg-white dark:bg-gray-800">
              <UpdateSalaryDetails
                refresh={refresh}
                setRefresh={setRefresh}
                onClose={() => setIsUpdating(false)}
                updateValues={updatedData}
              />
            </div>
          )}
        </GenericModal>
      )}
      {isAppraisal && (
        <GenericModal
          isOpen={isAppraisal}
          onClose={() => setIsAppraisal(false)}
          header="Add Appraisal"
          customWidth="1024px"
        >
          <div className="bg-white dark:bg-gray-800">
            <AddAppraisal
              employeeId={appraisalData?.id ?? 0}
              onClose={() => setIsAppraisal(false)}
            />
          </div>
        </GenericModal>
      )}

      {/* Full salary detail view modal */}
      {isViewing && viewData && (
        <GenericModal
          isOpen={isViewing}
          onClose={() => { setIsViewing(false); setViewData(null); }}
          header={`Salary Details — ${viewData.userName || viewData.employeeEmail}`}
          customWidth="1100px"
        >
          <SalaryDetailView data={viewData} />
        </GenericModal>
      )}

      {/* Salary structure calculation guide modal (markdown content) */}
      {isGuideOpen && (
        <GenericModal
          isOpen={isGuideOpen}
          onClose={() => setIsGuideOpen(false)}
          header="Salary Calculation Guide"
          customWidth="900px"
        >
          <div className="bg-white dark:bg-gray-800 p-6 max-h-[70vh] overflow-y-auto">
            {guideLoading ? (
              <div className="flex items-center justify-center py-10 text-slate-500 dark:text-slate-400">
                Loading calculation guide...
              </div>
            ) : guideError ? (
              <div className="flex flex-col items-center justify-center py-10 gap-3">
                <p className="text-sm text-red-500">{guideError}</p>
                <button
                  onClick={handleOpenGuide}
                  type="button"
                  className="inline-flex items-center justify-center h-[36px] px-4 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90 transition-all"
                >
                  Retry
                </button>
              </div>
            ) : (
              <div className="text-slate-700 dark:text-slate-300">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={guideMarkdownComponents}>
                  {guideContent}
                </ReactMarkdown>
              </div>
            )}
          </div>
        </GenericModal>
      )}
    </div>
  );
};
export default SalaryDetails;